import React, { ReactNode, useEffect, useState, useRef } from "react";
import clsx from "clsx";
import {
  SearchIcon,
  SlidersHorizontal,
  FileSpreadsheet,
  RotateCcw,
  ChevronDown,
  Check,
  Columns3,
  X,
} from "lucide-react";

import "./style.css";
import Button from "../button/button";
import { useDebouncedCallback } from "use-debounce";

export interface ReportToolbarProps {
  /** Search input value */
  searchValue?: string;
  /** Callback when search value changes */
  onSearchChange?: (value: string) => void;
  /** Callback on Enter key in search */
  onSearchEnter?: (value: string) => void;
  /** Placeholder text for search */
  searchPlaceholder?: string;

  /** Whether advanced filters panel is visible */
  showAdvancedFilters?: boolean;
  /** Callback to toggle advanced filters */
  onToggleAdvancedFilters?: () => void;
  /** Number of filters currently applied — shown as an orange badge on the
   * toggle and keeps it highlighted while the panel is closed. */
  activeFilterCount?: number;

  /** Callback to apply filters */
  onApplyFilters?: () => void;
  /** Callback to reset filters */
  onResetFilters?: () => void;
  /** Small print on the panel footer, left of the buttons */
  filterHint?: ReactNode;

  /** Callback to export excel */
  onExportExcel?: () => void;
  /** Whether export is in progress */
  isExporting?: boolean;

  /** Children to render as advanced filters content */
  children?: ReactNode;

  /** Additional CSS class */
  className?: string;

  hideAdvancedFilters?: boolean;

  onDebounceSearch?: (value: string) => void;

  onExportLoading?: boolean;

  chooseColumns?: {
    key: string;
    label: string;
  }[];
  hiddenColumns?: Set<string>;
  onToggleColumn?: (key: string) => void;
}

// Layout and look follow the smart-solution app's UIAdvancedFilterBar: one
// toolbar row (search / filter toggle / page actions) plus a collapsible field
// panel. Navy marks "active", orange is reserved for the count badge and the
// Apply action. Styles live in reportToolbar/style.css (.uiaf-*).
export const ReportToolbar: React.FC<ReportToolbarProps> = ({
  searchValue = "",
  onSearchChange,
  onSearchEnter,
  searchPlaceholder = "Search",
  showAdvancedFilters = false,
  onToggleAdvancedFilters,
  activeFilterCount = 0,
  onApplyFilters,
  onResetFilters,
  filterHint = "Filters apply instantly across all matching assets.",
  onExportExcel,
  isExporting = false,
  children,
  className = "",
  hideAdvancedFilters = false,
  onDebounceSearch,
  onExportLoading = false,
  chooseColumns,
  hiddenColumns,
  onToggleColumn,
}) => {
  const [localSearch, setLocalSearch] = useState(searchValue);
  const [showChooseColumns, setShowChooseColumns] = useState(false);

  // Ref on the columns menu wrapper, for the click-outside detector below.
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (searchValue !== localSearch) {
      setLocalSearch(searchValue);
    }
  }, [searchValue]);

  const debouncedSearch = useDebouncedCallback((value: string) => {
    onSearchChange?.(value);
    onDebounceSearch?.(value);
  }, 400);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowChooseColumns(false);
      }
    };

    // Only listen when the dropdown is actually open
    if (showChooseColumns) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showChooseColumns]);

  const filtersActive = showAdvancedFilters || activeFilterCount > 0;

  return (
    <div className="uiaf">
      {/* Toolbar row */}
      <div className={`uiaf-bar ${className}`.trim()}>
        <div className="uiaf-search-cluster">
          <SearchIcon size={16} className="uiaf-search-icon" />
          <input
            type="text"
            className="uiaf-search-input"
            placeholder={searchPlaceholder}
            value={localSearch}
            onChange={(e) => {
              const value = e.target.value;
              setLocalSearch(value);
              debouncedSearch(value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSearchEnter?.(localSearch);
              }
            }}
          />

          {localSearch && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                setLocalSearch("");
                debouncedSearch.cancel();
                onSearchChange?.("");
                onDebounceSearch?.("");
              }}
              className="uiaf-search-clear"
            >
              <X size={14} strokeWidth={2.5} />
            </button>
          )}
        </div>

        {hideAdvancedFilters ? null : (
          <button
            type="button"
            className={filtersActive ? "uiaf-toggle active" : "uiaf-toggle"}
            onClick={onToggleAdvancedFilters}
            aria-expanded={showAdvancedFilters}
          >
            <SlidersHorizontal size={15} strokeWidth={2} />
            <span>Advanced Filters</span>
            {activeFilterCount > 0 && (
              <span className="uiaf-badge">{activeFilterCount}</span>
            )}
            <ChevronDown
              size={16}
              strokeWidth={2}
              className={showAdvancedFilters ? "uiaf-caret open" : "uiaf-caret"}
            />
          </button>
        )}

        {chooseColumns && (
          <div className="uiaf-columns" ref={dropdownRef}>
            <button
              type="button"
              className={showChooseColumns ? "uiaf-toggle active" : "uiaf-toggle"}
              onClick={() => setShowChooseColumns((prev) => !prev)}
              aria-expanded={showChooseColumns}
            >
              <Columns3 size={16} strokeWidth={2} />
              <span>Choose Columns</span>
              <ChevronDown
                size={16}
                strokeWidth={2}
                className={showChooseColumns ? "uiaf-caret open" : "uiaf-caret"}
              />
            </button>

            {showChooseColumns && (
              <div className="uiaf-colmenu">
                <div className="uiaf-colmenu-title">Select Columns</div>
                <div className="uiaf-colmenu-list">
                  {chooseColumns.map((col) => {
                    const isChecked = !hiddenColumns?.has(col.key);
                    return (
                      <div
                        key={col.key}
                        role="menuitemcheckbox"
                        aria-checked={isChecked}
                        onClick={() => onToggleColumn?.(col.key)}
                        className={clsx("uiaf-colitem", isChecked && "checked")}
                      >
                        <span className="uiaf-colcheck">
                          {isChecked && <Check size={12} strokeWidth={3} />}
                        </span>
                        <span className="uiaf-colitem-label">{col.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="uiaf-actions">
          <Button
            leftIcon={<FileSpreadsheet size={17} />}
            onClick={onExportExcel}
            disabled={isExporting}
            className="h-10"
            variant="navy"
            loading={onExportLoading}
          >
            Export Excel
          </Button>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div className="uiaf-panel">
          <div className="uiaf-grid">{children}</div>

          <div className="uiaf-panel-footer">
            <span className="uiaf-hint">{filterHint}</span>
            <div className="uiaf-panel-buttons">
              <button type="button" className="uiaf-btn ghost" onClick={onResetFilters}>
                <RotateCcw size={14} strokeWidth={2} />
                Clear
              </button>
              <button type="button" className="uiaf-btn primary" onClick={onApplyFilters}>
                <SlidersHorizontal size={14} strokeWidth={2} />
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { ReportFilterChips } from "./FilterChips";
export type { ReportFilterChip } from "./FilterChips";
